import { useLayoutEffect, useState, Children, type ReactNode } from 'react';
import { NotificationBubble } from '../NotificationBubble/NotificationBubble';
import { hasBubbleId, addBubbleId } from '../../utils/bubbleEntranceStorage';
import { SItems, SStack } from './StackedAvatars.styles';

type Props = {
  children: ReactNode;
  /** When > 0, renders a `NotificationBubble` count anchored to the stack. */
  count?: number;
  /**
   * Stable identity for entrance-animation tracking. When provided, the count
   * bubble animates once per incident (skips on navigation remounts). When
   * omitted, the bubble never animates.
   */
  bubbleId?: string;
};

/**
 * Generic horizontal stack of overlapping items with an optional count bubble
 * anchored to the stack's right edge. Children render their own content; this
 * component only owns layout + the count.
 */
export const StackedAvatars = ({ children, count = 0, bubbleId }: Props) => {
  const [skipCountEntrance] = useState(() => (bubbleId ? hasBubbleId(bubbleId) : true));

  useLayoutEffect(() => {
    if (bubbleId && count > 0 && !skipCountEntrance) {
      addBubbleId(bubbleId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SStack>
      <SItems $depth={Children.count(children)}>{children}</SItems>
      {count > 0 && (
        <NotificationBubble notification={count} isDanger skipEntrance={skipCountEntrance} />
      )}
    </SStack>
  );
};
